import { useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';
import HookFormCheckbox from 'src/components/hookform/HookFormCheckbox';
import HookFormTextField from 'src/components/hookform/HookFormTextField';
import BackButton from 'src/components/BackButton';
import Iconify from 'src/components/Iconify';
import InfoAlert from 'src/components/InfoAlert';
import StatusChip from 'src/components/StatusChip';
import SuccessButton from 'src/components/SuccessButton';
import { SummaryValue } from 'src/components/summary/SummaryValue';
import { useEnvironmentPoliciesPage } from 'src/hooks/environmentWdacPolicy';
import { useDebounce } from 'src/queries/auth/useDebounce';
import { formatDate } from 'src/utils/formatTime';
import {
  getBasePolicyModeColor,
  getBasePolicyModeLabel,
  getBasePolicyModeOptions,
} from 'src/utils/enums/basePolicyMode';
import { WDAC_POLICY_TYPE } from 'src/utils/enums/wdacPolicyType';
import { ENVIRONMENT_DEFAULT_HELP } from './EditManagedBasePolicyForm';

const ADD_EXISTING = { id: 'addExisting', policyName: '+ Add Existing Policy' };
const isAddingExisting = basePolicy => basePolicy?.id === ADD_EXISTING.id;

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const VERSION_REGEX = /^\d+(\.\d+){1,3}$/;

// Adding an existing policy asks for everything detection would otherwise have filled in, so those
// fields are only required once it is picked.
const schema = Yup.object().shape({
  basePolicy: Yup.mixed().nullable().required('Select a base policy or add an existing one'),
  policyName: Yup.string()
    .trim()
    .when('basePolicy', {
      is: isAddingExisting,
      then: field => field.required('Policy name is required'),
    }),
  policyId: Yup.string()
    .trim()
    .when('basePolicy', {
      is: isAddingExisting,
      then: field =>
        field
          .required('Policy ID is required')
          .matches(GUID_REGEX, 'Must be a valid GUID: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'),
    }),
  basePolicyMode: Yup.string().when('basePolicy', {
    is: isAddingExisting,
    then: field => field.required('Base policy mode is required'),
  }),
  policyVersion: Yup.string()
    .trim()
    .when('basePolicy', {
      is: isAddingExisting,
      then: field =>
        field
          .required('Version is required')
          .matches(VERSION_REGEX, 'Use a numeric version like 1.0.0.0'),
    }),
  policyDescription: Yup.string().trim(),
  environmentDefault: Yup.boolean(),
});

const DEFAULT_VALUES = {
  basePolicy: null,
  policyName: '',
  policyId: '',
  basePolicyMode: 'Enforce',
  policyVersion: '1.0.0.0',
  policyDescription: '',
  environmentDefault: false,
};

const POLICIES_LIMIT = 25;

const ADD_EXISTING_NOTE =
  'Any policy added here should already exist in your organization, even if it has not been detected yet. Input its details below.';

const SelectedBasePolicyCard = ({ policy }) => {
  const modeColor = getBasePolicyModeColor(policy.basePolicyMode);

  return (
    <Box
      sx={{
        backgroundColor: '#454545',
        border: '1px solid #5B5B5B',
        borderRadius: '8px',
        p: 1.5,
      }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Iconify icon="mdi:shield-outline" width={18} height={18} color="#87C8E0" />
        <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '0.88rem' }}>
          {policy.policyName}
        </Typography>
        {modeColor ? (
          <StatusChip
            label={getBasePolicyModeLabel(policy.basePolicyMode)}
            color={modeColor}
            backgroundColor={`${modeColor}1F`}
            sx={{ height: 20, fontSize: '0.65rem' }}
          />
        ) : null}
      </Stack>
      <Typography sx={{ color: '#9CA3AF', fontSize: '0.7rem', mt: 0.25 }}>
        {policy.policyId}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
          gap: 1.25,
          mt: 1,
        }}>
        <SummaryValue compact label="Version">
          {policy.policyVersion}
        </SummaryValue>
        <SummaryValue compact label="Devices">
          {policy.devicesCount ?? 0}
        </SummaryValue>
        <SummaryValue compact label="Visibility">
          {policy.isHidden ? 'Hidden' : 'Visible'}
        </SummaryValue>
        <SummaryValue compact label="First seen">
          {formatDate(policy.createdAt, 'yyyy-MM-dd')}
        </SummaryValue>
      </Box>
    </Box>
  );
};

const renderOption = (props, option) =>
  isAddingExisting(option) ? (
    <Box component="li" {...props} key={option.id}>
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }} noWrap>
          {option.policyName}
        </Typography>
        <Typography variant="caption" sx={{ color: 'primary.main' }} noWrap>
          Provide details for an existing policy we haven&apos;t detected yet
        </Typography>
      </Stack>
    </Box>
  ) : (
    <Box component="li" {...props} key={option.id}>
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {option.policyName}
        </Typography>
        <Typography variant="caption" sx={{ color: '#9CA3AF' }} noWrap>
          {`${option.policyId} · ${option.devicesCount ?? 0} devices`}
        </Typography>
      </Stack>
    </Box>
  );

// Bringing a base policy under management, either one devices already report or one they run that
// has not been detected yet.
const UseExistingPolicyForm = ({ loading, onCancel, onUseDetected, onAddExisting }) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(schema),
  });

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { isValid },
  } = formMethods;

  const { data, isFetching } = useEnvironmentPoliciesPage({
    apiQuery: {
      policyTypes: [WDAC_POLICY_TYPE.BasePolicy],
      isHidden: [false],
      isReported: true,
      isPortalManaged: false,
      search: debouncedSearch.trim() || undefined,
      sortColumn: 'Devices',
      sortDesc: true,
    },
    limit: POLICIES_LIMIT,
  });
  const policies = data?.results || [];

  const basePolicy = watch('basePolicy');
  const addingExisting = isAddingExisting(basePolicy);

  // A pick brings the description the policy already has, unless a note has been typed over the
  // previous pick's.
  const pick = (option, onChange) => {
    if (getValues('policyDescription') === (basePolicy?.policyDescription || '')) {
      setValue('policyDescription', option?.policyDescription || '', { shouldValidate: true });
    }
    onChange(option);
  };

  const submit = values => {
    const settings = {
      policyDescription: values.policyDescription?.trim() || null,
      environmentDefault: values.environmentDefault,
    };

    if (addingExisting) {
      onAddExisting({
        ...settings,
        policyName: values.policyName.trim(),
        policyId: values.policyId.trim(),
        basePolicyMode: values.basePolicyMode,
        policyVersion: values.policyVersion.trim(),
      });
    } else {
      onUseDetected(values.basePolicy, settings);
    }
  };

  const nothingDetected = !isFetching && !debouncedSearch.trim() && policies.length === 0;

  return (
    <form autoComplete="off" onSubmit={handleSubmit(submit)}>
      <Stack gap={2}>
        <Controller
          name="basePolicy"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              value={value}
              // A pick stays on offer after a search that would no longer return it.
              options={
                value &&
                !isAddingExisting(value) &&
                !policies.some(policy => policy.id === value.id)
                  ? [ADD_EXISTING, value, ...policies]
                  : [ADD_EXISTING, ...policies]
              }
              loading={isFetching}
              // The server does the filtering, and adding an existing policy stays on offer whatever
              // it returns.
              filterOptions={options => options}
              onChange={(event, option) => pick(option, onChange)}
              onInputChange={(event, input, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setSearch(input);
                }
              }}
              getOptionLabel={option => option?.policyName || ''}
              isOptionEqualToValue={(option, selected) => option.id === selected?.id}
              renderOption={renderOption}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Base policy"
                  size="small"
                  placeholder="Search by name or policy ID"
                  helperText={
                    nothingDetected
                      ? 'Every base policy detected in this environment is already managed.'
                      : ''
                  }
                />
              )}
            />
          )}
        />

        {basePolicy && !addingExisting ? <SelectedBasePolicyCard policy={basePolicy} /> : null}

        {addingExisting ? (
          <>
            <InfoAlert sx={{ py: 0.25 }}>
              <Typography variant="caption">{ADD_EXISTING_NOTE}</Typography>
            </InfoAlert>
            <HookFormTextField
              name="policyName"
              label="Policy name"
              formMethods={formMethods}
              size="small"
              fullWidth
            />
            <HookFormTextField
              name="policyId"
              label="Policy ID"
              formMethods={formMethods}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              size="small"
              fullWidth
            />
            <HookFormTextField
              name="basePolicyMode"
              label="Base policy mode"
              formMethods={formMethods}
              select
              size="small"
              fullWidth
              options={getBasePolicyModeOptions()}
            />
            <HookFormTextField
              name="policyVersion"
              label="Policy version"
              formMethods={formMethods}
              size="small"
              fullWidth
            />
          </>
        ) : null}

        <HookFormTextField
          name="policyDescription"
          label="Policy description note"
          formMethods={formMethods}
          placeholder="Optional"
          size="small"
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
        />
        <HookFormCheckbox
          name="environmentDefault"
          label="Environment default"
          helperText={ENVIRONMENT_DEFAULT_HELP}
          formMethods={formMethods}
        />

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <BackButton disabled={loading} onClick={onCancel}>
            Cancel
          </BackButton>
          <SuccessButton type="submit" disabled={loading || !isValid} loading={loading}>
            Use Policy
          </SuccessButton>
        </Stack>
      </Stack>
    </form>
  );
};

UseExistingPolicyForm.propTypes = {
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onUseDetected: PropTypes.func.isRequired,
  onAddExisting: PropTypes.func.isRequired,
};

export default UseExistingPolicyForm;
